<template>
  <b-row>
    <b-col sm="12">
      <b-card header-tag="header" footer-tag="footer">
        <b-row>
          <b-col>
            <h4 class="card-title mb-0">Cierre y Apertura de Ejercicio</h4>
            <div class="small text-muted">Generación de asientos de cierre y apertura de ejercicio contable.</div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Filtros -->
      <b-card header-tag="header" footer-tag="footer">
        <b-row class="align-items-end">
          <b-col md="2">
            <label class="filter-label">Empresa *</label>
            <b-form-select size="sm" v-model="filters.business_id" :options="arr.businesses" @change="onFilterChange()"></b-form-select>
          </b-col>
          <b-col md="1">
            <label class="filter-label">Tipo *</label>
            <b-form-select size="sm" v-model="filters.declared" :options="arr.select.declared" @change="onFilterChange()"></b-form-select>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Inicio del ejercicio</label>
            <b-form-input type="date" size="sm" v-model="filters.date_start" @change="onStartDateChange()"></b-form-input>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Fecha de cierre</label>
            <b-form-input type="date" size="sm" v-model="filters.date_close" @change="onCloseDateChange()"></b-form-input>
          </b-col>
          <b-col md="2">
            <label class="filter-label">Fecha de apertura</label>
            <b-form-input type="date" size="sm" v-model="filters.date_open"></b-form-input>
          </b-col>
          <b-col cols="auto" class="d-flex align-items-end">
            <b-button variant="dark" size="sm" @click="preview()" :disabled="loading">
              <b-icon icon="search" class="mr-1"></b-icon>
              Previsualizar
            </b-button>
          </b-col>
        </b-row>

        <!-- Bloque informativo (antes de previsualizar) -->
        <div v-if="!previewData" class="closing-info mt-4">
          <div class="closing-info-title">¿Qué va a suceder?</div>

          <div class="closing-info-period mt-2 mb-3">
            Se analizarán todos los movimientos contables del período
            <b>{{ formatDate(filters.date_start) }}</b> al <b>{{ formatDate(filters.date_close) }}</b>.
            El inicio de ejercicio marca desde dónde se calculan los saldos; generalmente coincide con la fecha de apertura del ejercicio anterior.
          </div>

          <b-row>
            <b-col md="6">
              <div class="closing-info-step">
                <div class="closing-info-step-num">1</div>
                <div class="closing-info-step-body">
                  <div class="closing-info-step-label">Asiento de Cierre <span class="text-muted font-weight-normal">· {{ formatDate(filters.date_close) }}</span></div>
                  <div class="closing-info-step-text">
                    Salda todas las cuentas de <b>Ingresos</b> y <b>Egresos</b> acumulados en el período contra la cuenta de <b>Resultado del Ejercicio</b>, dejando esas cuentas en cero.
                    El período quedará <b>bloqueado</b>: no se podrán registrar nuevos asientos con fechas anteriores a este cierre.
                  </div>
                </div>
              </div>
            </b-col>
            <b-col md="6">
              <div class="closing-info-step">
                <div class="closing-info-step-num">2</div>
                <div class="closing-info-step-body">
                  <div class="closing-info-step-label">Asiento de Apertura <span class="text-muted font-weight-normal">· {{ formatDate(filters.date_open) }}</span></div>
                  <div class="closing-info-step-text">
                    Reabre las cuentas de <b>Activo</b>, <b>Pasivo</b> y <b>Patrimonio</b> con los saldos al cierre, incorporando el resultado del ejercicio.
                    Esta fecha pasa a ser el nuevo <b>inicio de ejercicio</b> para el próximo cierre.
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-card>

      <!-- Previsualización -->
      <div v-if="previewData">

        <!-- Asiento de Cierre -->
        <b-card header-tag="header" footer-tag="footer">
          <b-row class="mb-3">
            <b-col>
              <h5 class="mb-0">
                Asiento de Cierre
                <small class="text-muted ml-2">{{ formatDate(filters.date_close) }}</small>
              </h5>
              <small class="text-muted">Salda todas las cuentas de resultado contra Resultado del Ejercicio</small>
            </b-col>
            <b-col cols="auto">
              <b-badge :variant="previewData.closing.net_result >= 0 ? 'success' : 'danger'" style="font-size:13px; padding:6px 12px;">
                {{ previewData.closing.net_result >= 0 ? 'Ganancia' : 'Pérdida' }}:
                {{ formatCurrency(Math.abs(previewData.closing.net_result)) }}
              </b-badge>
            </b-col>
          </b-row>

          <b-table class="mb-3"
                   responsive="sm"
                   head-variant="dark"
                   :hover="true"
                   :small="true"
                   :items="previewData.closing.lines"
                   :fields="tableFields"
                   :tbody-tr-class="rowClass">
            <template v-slot:cell(code)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{ data.item.accounting_accounts_code }}
              </span>
            </template>
            <template v-slot:cell(name)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{ data.item.accounting_accounts_name }}
              </span>
            </template>
            <template v-slot:cell(debit)="data">
              <b v-if="data.item.type == 'Debe'" class="accounting-closing-amount">{{ formatCurrency(data.item.amount) }}</b>
            </template>
            <template v-slot:cell(credit)="data">
              <b v-if="data.item.type == 'Haber'" class="accounting-closing-amount">{{ formatCurrency(data.item.amount) }}</b>
            </template>
          </b-table>

          <div class="d-flex justify-content-end mb-3">
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:#fff3cd;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Debe</small>
              <strong style="font-size:15px;">{{ formatCurrency(previewData.closing.total_debit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2" style="min-width:150px; background:#d1ecf1;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Haber</small>
              <strong style="font-size:15px;">{{ formatCurrency(previewData.closing.total_credit) }}</strong>
            </div>
          </div>

          <hr>

          <div v-if="!generated.closing">
            <b-row class="align-items-end">
              <b-col md="4">
                <label><b>Referencia</b></label>
                <b-form-input type="text" size="sm" v-model="crud.closing.reference" placeholder="Ej: CIERRE-2024"></b-form-input>
              </b-col>
              <b-col md="6">
                <label><b>Observaciones</b></label>
                <b-form-input type="text" size="sm" v-model="crud.closing.observations"></b-form-input>
              </b-col>
              <b-col md="2" class="d-flex align-items-end">
                <b-button variant="dark" size="sm" block @click="generateClosing()" :disabled="loading">
                  Generar cierre
                </b-button>
              </b-col>
            </b-row>
          </div>
          <b-alert v-else variant="success" show class="mb-0">
            <b-icon icon="check-circle" class="mr-1"></b-icon>
            Asiento de cierre generado: <b>{{ generated.closing.reference }}</b>
          </b-alert>
        </b-card>

        <!-- Asiento de Apertura -->
        <b-card header-tag="header" footer-tag="footer">
          <b-row class="mb-3">
            <b-col>
              <h5 class="mb-0">
                Asiento de Apertura
                <small class="text-muted ml-2">{{ formatDate(filters.date_open) }}</small>
              </h5>
              <small class="text-muted">Re-abre las cuentas patrimoniales con los saldos al cierre</small>
            </b-col>
          </b-row>

          <b-table class="mb-3"
                   responsive="sm"
                   head-variant="dark"
                   :hover="true"
                   :small="true"
                   :items="previewData.opening.lines"
                   :fields="tableFields"
                   :tbody-tr-class="rowClass">
            <template v-slot:cell(code)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{ data.item.accounting_accounts_code }}
              </span>
            </template>
            <template v-slot:cell(name)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{ data.item.accounting_accounts_name }}
              </span>
            </template>
            <template v-slot:cell(debit)="data">
              <b v-if="data.item.type == 'Debe'" class="accounting-closing-amount">{{ formatCurrency(data.item.amount) }}</b>
            </template>
            <template v-slot:cell(credit)="data">
              <b v-if="data.item.type == 'Haber'" class="accounting-closing-amount">{{ formatCurrency(data.item.amount) }}</b>
            </template>
          </b-table>

          <div class="d-flex justify-content-end mb-3">
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:#fff3cd;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Debe</small>
              <strong style="font-size:15px;">{{ formatCurrency(previewData.opening.total_debit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2" style="min-width:150px; background:#d1ecf1;">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Haber</small>
              <strong style="font-size:15px;">{{ formatCurrency(previewData.opening.total_credit) }}</strong>
            </div>
          </div>

          <hr>

          <div v-if="!generated.opening">
            <b-row class="align-items-end">
              <b-col md="4">
                <label><b>Referencia</b></label>
                <b-form-input type="text" size="sm" v-model="crud.opening.reference" placeholder="Ej: APERTURA-2025"></b-form-input>
              </b-col>
              <b-col md="6">
                <label><b>Observaciones</b></label>
                <b-form-input type="text" size="sm" v-model="crud.opening.observations"></b-form-input>
              </b-col>
              <b-col md="2" class="d-flex align-items-end">
                <b-button variant="dark" size="sm" block @click="generateOpening()" :disabled="loading">
                  Generar apertura
                </b-button>
              </b-col>
            </b-row>
          </div>
          <b-alert v-else variant="success" show class="mb-0">
            <b-icon icon="check-circle" class="mr-1"></b-icon>
            Asiento de apertura generado: <b>{{ generated.opening.reference }}</b>
          </b-alert>
        </b-card>
      </div>

      <b-card header-tag="header" footer-tag="footer">
        <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
          <i class="fa fa-angle-double-left"></i>
          Volver
        </b-button>
      </b-card>
    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Helper from '@/handler/helper'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import moment from 'moment'

  export default {
    data: () => {
      return {
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountingClosing',
          elements: {}
        },
        filters: {
          date_start: '',
          date_close: '',
          date_open: '',
          business_id: null,
          declared: null,
        },
        arr: {
          businesses: [],
          select: {
            declared: [
              { value: null, text: 'Seleccionar...' },
              { value: true, text: 'D' },
              { value: false, text: 'ND' },
            ],
          }
        },
        tableFields: [
          { key: 'code',   label: 'Código',  class: 'align-middle' },
          { key: 'name',   label: 'Nombre',  class: 'align-middle' },
          { key: 'debit',  label: 'Debe',    class: 'align-middle text-right' },
          { key: 'credit', label: 'Haber',   class: 'align-middle text-right' },
        ],
        previewData: null,
        loading: false,
        generated: {
          closing: null,
          opening: null,
        },
        crud: {
          closing: { reference: '', observations: 'Asiento de cierre de ejercicio' },
          opening: { reference: '', observations: 'Asiento de apertura de ejercicio' },
        },
      }
    },
    created() {
      Helper.hasAccessView(this.access)
    },
    mounted() {
      this.loadBusinesses()
    },
    methods: {
      moment,
      loadBusinesses() {
        serviceAPI.obtenerEmpresas()
          .then(response => {
            const items = response.data || []
            this.arr.businesses = items.map(b => ({ value: b.id, text: b.name || b.reference || String(b.id) }))
            if(this.arr.businesses.length) {
              this.filters.business_id = this.arr.businesses[0].value
            }
            this.initDates()
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
          })
      },
      initDates() {
        const year = moment().year() - 1
        this.filters.date_start = year + '-01-01'
        this.filters.date_close = year + '-12-31'
        this.filters.date_open  = (year + 1) + '-01-01'
        this.crud.closing.reference = 'CIERRE-' + year
        this.crud.opening.reference = 'APERTURA-' + (year + 1)
      },
      onStartDateChange() {
        if(this.filters.date_start) {
          const year = moment(this.filters.date_start).year()
          this.filters.date_close = year + '-12-31'
          this.filters.date_open  = (year + 1) + '-01-01'
          this.crud.closing.reference = 'CIERRE-' + year
          this.crud.opening.reference = 'APERTURA-' + (year + 1)
        }
        this.previewData = null
        this.generated = { closing: null, opening: null }
      },
      onCloseDateChange() {
        if(this.filters.date_close) {
          const next = moment(this.filters.date_close).add(1, 'day').format('YYYY-MM-DD')
          this.filters.date_open = next
        }
        this.previewData = null
        this.generated = { closing: null, opening: null }
      },
      onFilterChange() {
        this.previewData = null
        this.generated = { closing: null, opening: null }
      },
      rowClass(item) {
        if(!item) return ''
        return item.type === 'Debe' ? 'table-warning' : 'table-info'
      },
      formatDate(date) {
        return date ? moment(date).format('DD/MM/YYYY') : ''
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0)
      },
      preview() {
        if(!this.filters.business_id) {
          this.$awn.alert('Debe seleccionar la empresa')
          return
        }
        if(this.filters.declared === null || this.filters.declared === undefined) {
          this.$awn.alert('Debe seleccionar el tipo (Declarado / No Declarado)')
          return
        }
        if(!this.filters.date_close) {
          this.$awn.alert('Debe indicar la fecha de cierre')
          return
        }
        this.loading = true
        this.previewData = null
        this.generated = { closing: null, opening: null }

        serviceAPI.previewCierreApertura({
          date_start:  this.filters.date_start,
          date_close:  this.filters.date_close,
          date_open:   this.filters.date_open,
          business_id: this.filters.business_id,
          declared:    this.filters.declared,
        })
        .then(response => {
          this.previewData = response.data
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      generateClosing() {
        if(!this.crud.closing.reference) {
          this.$awn.alert('Debe indicar la referencia del asiento de cierre')
          return
        }
        this.loading = true
        serviceAPI.generarCierre({
          date_start:   this.filters.date_start,
          date_close:   this.filters.date_close,
          reference:    this.crud.closing.reference,
          observations: this.crud.closing.observations,
          business_id:  this.filters.business_id,
          declared:     this.filters.declared,
        })
        .then(response => {
          this.generated.closing = response.data
          this.$awn.success('Asiento de cierre generado con éxito')
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      generateOpening() {
        if(!this.crud.opening.reference) {
          this.$awn.alert('Debe indicar la referencia del asiento de apertura')
          return
        }
        this.loading = true
        serviceAPI.generarApertura({
          date_start:   this.filters.date_start,
          date_close:   this.filters.date_close,
          date_open:    this.filters.date_open,
          reference:    this.crud.opening.reference,
          observations: this.crud.opening.observations,
          business_id:  this.filters.business_id,
          declared:     this.filters.declared,
        })
        .then(response => {
          this.generated.opening = response.data
          this.$awn.success('Asiento de apertura generado con éxito')
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
    }
  }
</script>
<style scoped>
  .accounting-closing-amount {
    font-size: 13px;
  }
  .filter-label {
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: #495057;
    display: block;
  }
  .closing-info {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 16px 20px;
  }
  .closing-info-title {
    font-size: 13px;
    font-weight: 700;
    color: #495057;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  .closing-info-period {
    font-size: 12px;
    color: #495057;
    border-left: 3px solid #343a40;
    padding-left: 10px;
  }
  .closing-info-step {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 4px;
  }
  .closing-info-step-num {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    background: #343a40;
    color: #fff;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
  }
  .closing-info-step-label {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .closing-info-step-text {
    font-size: 12px;
    color: #495057;
    line-height: 1.5;
  }
</style>
